import Logger = require("js-logger");
import CampaignsModel from "slotworks/integration/revolver/model/campaigns-model";
import RevolverModel from "slotworks/integration/revolver/model/revolver-model";
import { AwardData } from "slotworks/integration/revolver/schema/init-record-schema";
import { ResultsAndDetail, SpinResponseSchema } from "slotworks/integration/revolver/schema/spin-record-schema";
import Gameplay from "slotworks/model/gameplay/gameplay-model";
import FreespinRecord from "slotworks/model/gameplay/records/freespin-record";
import Record from "slotworks/model/gameplay/records/record";
import Result from "slotworks/model/gameplay/records/results/result";
import SpinRecord, { ExpansionDefinition } from "slotworks/model/gameplay/records/spin-record";
import SlotDefinition from "slotworks/model/slot-definition";
import BuilderManager from "slotworks/server/builders/builder-manager";
import { RecordBuilder } from "slotworks/server/builders/record-builder";
import SymbolModel from "slotworks/server/symbols/symbol-model";
import Point from "slotworks/utils/geom/point";
import WrapperMediator from "slotworks/integration/revolver/wrapper/wrapper-mediator";
import gameState from "slotworks/model/game-state";

export class SpinRecordBuilder implements RecordBuilder {

    public filter(data: any): boolean {
        let spinResponse: SpinResponseSchema;

        if (data && data.data) {
            spinResponse = data.data as SpinResponseSchema;
        }

        return spinResponse && spinResponse.RESULTS !== undefined && (RevolverModel.isSpin(spinResponse.RESULTS.game) || RevolverModel.isFreespin(spinResponse.RESULTS.game));
    }

    public build(gameplay: Gameplay, data: any): Record[] {
        let spinResponse: SpinResponseSchema;

        if (data && data.data) {
            spinResponse = data.data as SpinResponseSchema;
            WrapperMediator.roundStarted(spinResponse.RESULTS.round_id);
            WrapperMediator.spinResponseReceived(spinResponse);
        }

        gameplay.id = spinResponse.RESULTS.round_id.toString();

        const isFreeSpin = RevolverModel.isFreespin(spinResponse.RESULTS.game);

        const win = parseFloat(spinResponse.RESULTS.total_win_cash as any);
        gameplay.totalCreditsWon += win;
        gameState.updateNetPosition(win);
        if(isFreeSpin) {
            gameplay.totalFreespinsCreditsWon += win;
        } else {
            gameplay.totalBaseCreditsWon += win;
        }
 
        gameplay.balance = spinResponse.USER.balance;

        const spinRecord = isFreeSpin ? new FreespinRecord() : new SpinRecord();

        const aperture = spinResponse.RESULTS.eval.aperture as string[][];
        this.processAperture(aperture, spinRecord);

        if (isFreeSpin) {
            spinRecord.reelset = SlotDefinition.reelsets.get("freespins");
        } else {
            spinRecord.reelset = SlotDefinition.reelsets.get("base");
        }

        spinRecord.creditsWon = parseFloat(spinResponse.RESULTS.total_win_cash as any);
        spinRecord.gameName = spinResponse.RESULTS.game;

        this.processWins(spinRecord, spinResponse);

        if (spinRecord instanceof FreespinRecord) {
            this.processFreespinsRecord(spinRecord, spinResponse);
        }

        this.processOtherResult(spinRecord, spinResponse);

        this.expandingWilds(spinRecord, spinResponse);

        this.stickyWilds(spinRecord, spinResponse);

        if(CampaignsModel.isFreespinsActive()) {
            try {
                CampaignsModel.freespins.totalWin += parseFloat(spinResponse.RESULTS.total_win_cash as any);
            }catch(e) {

            }
        }

        if(spinResponse.RESULTS.updated_free_spin_campaign_details)
        {
            const freespin = spinResponse.RESULTS.updated_free_spin_campaign_details;
            const awardData = typeof freespin.awardData === "string" ? JSON.parse(freespin.awardData) as AwardData : freespin.awardData as AwardData;
            CampaignsModel.freespins.total = awardData.total;
            CampaignsModel.freespins.remaining = awardData.count;
            CampaignsModel.freespins.isComplete = awardData.count === 0;
        }

        return [spinRecord];
    }

    private processAperture(aperture: string[][], spinRecord: SpinRecord) {
        const trimmed: string[][] = [];
        for (const reel of aperture) {
            trimmed.push([]);
            for (const symbol of reel) {
                trimmed[trimmed.length - 1].push(symbol);
            }
        }

        trimmed.forEach((outer: string[], i: number, grid: string[][]) => {
            // If revolver aperture does not match expected matrix grid dimensions, time off excess symbols
            if (trimmed[i].length !== SlotDefinition.matrixGrid[i]) {
                outer.shift();
                outer.pop();
            }
            outer.forEach((inner: string, j: number, reel: string[]) => {
                reel[j] = SymbolModel.convertSymbolToSlotworks(inner);
            });
        });

        spinRecord.grid = trimmed;
    }

    private processWins(spinRecord: SpinRecord, spinResponse: SpinResponseSchema) {
        let results: Result[] = [];

        if (spinResponse.RESULTS.eval.wins !== undefined) {
            for (const detail of spinResponse.RESULTS.eval.wins.detail) {
                const builtResults: Result[] = BuilderManager.buildResult(spinRecord, {
                    detail,
                    results: spinResponse.RESULTS
                } as ResultsAndDetail);
                results = results.concat(builtResults);
            }
        }

        spinRecord.results = spinRecord.results.concat(results);

        spinRecord.freespinsWon = spinResponse.RESULTS.eval.freeSpinsCount ? spinResponse.RESULTS.eval.freeSpinsCount : 0;
        if (spinRecord.freespinsWon > 0) {
            spinRecord.freespinsTriggered = true;
        }
    }

    private processFreespinsRecord(spinRecord: FreespinRecord, spinResponse: SpinResponseSchema) {
        if (typeof spinResponse.RESULTS.state.total === "undefined") {
            const total = spinResponse.RESULTS.state.remain + spinResponse.RESULTS.state.spins;
            Logger.log("Error: total freespins is undefined. Setting it to " + total);
            spinResponse.RESULTS.state.total = total;
        }

        spinRecord.totalFreespins = spinResponse.RESULTS.state.total;
        spinRecord.remainingFreespins = spinResponse.RESULTS.state.remain;
        spinRecord.isFreespinsComplete = spinResponse.RESULTS.state.is_complete;
    }

    private processOtherResult(spinRecord: SpinRecord, spinResponse: SpinResponseSchema) {
        const results = BuilderManager.buildResult(spinRecord, spinResponse);

        spinRecord.results = spinRecord.results.concat(results);
    }

    private stickyWilds(spinRecord: SpinRecord, spinResponse: SpinResponseSchema) {
        const stickyWilds = spinResponse.RESULTS.state.latestStickedWilds;
        if (stickyWilds && stickyWilds.length) {
            const gridPositions = RevolverModel.convertPositionsToGridPositions(stickyWilds);

            for (const position of gridPositions) {
                spinRecord.grid[position.x][position.y] = "S" + spinRecord.grid[position.x][position.y];
            }
        }
    }

    private expandingWilds(spinRecord: SpinRecord, spinResponse: SpinResponseSchema) {
        const expandingReels = spinResponse.RESULTS.eval.expanding_reels;
        if (expandingReels && expandingReels.length) {

            spinRecord.expansions = [];

            for (const expandingReel of expandingReels) {

                let source;
                const expansions = [];
                const target = new Point(expandingReel + 1, 1);

                for (let y = 0; y < spinRecord.grid[expandingReel].length; y++) {
                    const symbol = spinRecord.grid[expandingReel][y];
                    const symbolDef = SlotDefinition.getSymbolDefinition(symbol);
                    if (symbolDef.wild && !source) {
                        source = new Point(expandingReel + 1, y + 1);
                    }

                    expansions.push(new Point(expandingReel + 1, y + 1));
                }

                const expansionDefinition = new ExpansionDefinition(source, expansions, target);
                spinRecord.expansions.push(expansionDefinition);
            }
        }
    }
}

export default new SpinRecordBuilder();



// WEBPACK FOOTER //
// ./~/ifdef-loader/ifdef-loader.js??ref--0-1!./~/slotworks/integration/revolver/builders/records/spin-record-builder.ts